import React from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { getSlugForLanguage } from '../App';

// SEO-Rich Content Sections Component
// Adds 1,500-2,000 words of SEO-optimized content per page
// With intelligent fallback for missing translations

// Some i18n seo.* content blocks were authored under legacy keys that do not
// match the variant prop name. Map variant -> seo key so they resolve.
const SEO_KEY_ALIAS = {
  numberWheel: 'numberPicker',
  teamGenerator: 'team',
  letterWheel: 'letterPicker',
};

const SEOContentSections = ({ variant }) => {
  const { t, language } = useLanguage();

  const seoKey = SEO_KEY_ALIAS[variant] || variant;

  // Load an i18n object block (t() already falls back to English internally).
  // t() echoes the key STRING when the path is missing in both the active
  // language and the English fallback; normalize that (and any non-object) to
  // null so downstream `.map()` calls can never crash on a string.
  const loadContent = (key) => {
    try {
      const value = t(`seo.${seoKey}.${key}`, { returnObjects: true });
      return (value == null || typeof value === 'string') ? null : value;
    } catch (e) {
      return null;
    }
  };

  const content = {
    hero: loadContent('hero'),
    benefits: loadContent('benefits'),
    howTo: loadContent('howTo'),
    useCases: loadContent('useCases'),
    whyBetter: loadContent('whyBetter')
  };

  // If no content at all, don't render (variant not supported yet)
  if (!content.hero || typeof content.hero === 'string') {
    return null;
  }

  return (
    <div className="seo-content-sections">
      {/* Hero Section - Above the Fold */}
      <section className="seo-hero" itemScope itemType="https://schema.org/WebApplication">
        <h2 itemProp="name">{content.hero?.title || t(`variants.${variant}.title`)}</h2>
        <div className="hero-content" itemProp="description">
          {content.hero?.description && <p className="lead-text">{content.hero.description}</p>}
          {content.hero?.subtext && <p>{content.hero.subtext}</p>}
        </div>

        {/* Quick Stats */}
        <div className="quick-stats">
          <div className="stat">
            <span className="stat-number">{content.hero?.stats?.users || '50,000+'}</span>
            <span className="stat-label">{t('seo.common.users')}</span>
          </div>
          <div className="stat">
            <span className="stat-number">{content.hero?.stats?.spins || '1M+'}</span>
            <span className="stat-label">{t('seo.common.spins')}</span>
          </div>
          <div className="stat">
            <span className="stat-number">100%</span>
            <span className="stat-label">{t('seo.common.free')}</span>
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="seo-benefits">
        <h2>{t('seo.common.whyChoose')}</h2>
        <div className="benefits-grid">
          {Array.isArray(content.benefits) && content.benefits.map((benefit, idx) => (
            <div key={idx} className="benefit-card">
              <div className="benefit-icon">{benefit.icon || '✓'}</div>
              <h3>{benefit.title}</h3>
              <p>{benefit.description}</p>
            </div>
          ))}
        </div>
      </section>

      {/* How To Use Section */}
      <section className="seo-how-to" itemScope itemType="https://schema.org/HowTo">
        <h2 itemProp="name">{content.howTo?.title || t('seo.common.howToUse')}</h2>
        <meta itemProp="totalTime" content="PT2M" />

        <div className="how-to-steps">
          {Array.isArray(content.howTo?.steps) && content.howTo.steps.map((step, idx) => (
            <div key={idx} className="step" itemProp="step" itemScope itemType="https://schema.org/HowToStep">
              <div className="step-number">{idx + 1}</div>
              <div className="step-content">
                <h3 itemProp="name">{step.title}</h3>
                <p itemProp="text">{step.description}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Use Cases Section */}
      <section className="seo-use-cases">
        <h2>{content.useCases?.title || t('seo.common.useCases')}</h2>
        <div className="use-cases-grid">
          {Array.isArray(content.useCases?.cases) && content.useCases.cases.map((useCase, idx) => (
            <div key={idx} className="use-case-card">
              <div className="use-case-icon">{useCase.icon}</div>
              <h3>{useCase.title}</h3>
              <p>{useCase.description}</p>
              <div className="use-case-example">
                <strong>{t('seo.common.example')}:</strong> {useCase.example}
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Why Better Section */}
      <section className="seo-why-better">
        <h2>{content.whyBetter?.title || t('seo.common.whyBetter')}</h2>
        <div className="comparison-grid">
          {Array.isArray(content.whyBetter?.points) && content.whyBetter.points.map((point, idx) => (
            <div key={idx} className="comparison-card">
              <h3>{point.title}</h3>
              <div className="comparison-content">
                <div className="other-tools">
                  <span className="label">{t('seo.common.otherTools')}</span>
                  <span className="value negative">{point.others}</span>
                </div>
                <div className="our-tool">
                  <span className="label">wheel.expert</span>
                  <span className="value positive">{point.ours}</span>
                </div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Social Proof Section */}
      <section className="seo-social-proof">
        <h2>{t('seo.common.trustedBy')}</h2>
        <div className="social-proof-content">
          <div className="testimonials">
            {Array.isArray(content.hero?.testimonials) && content.hero.testimonials.map((testimonial, idx) => (
              <blockquote key={idx} className="testimonial">
                <p>"{testimonial.text}"</p>
                <cite>— {testimonial.author}, {testimonial.role}</cite>
              </blockquote>
            ))}
          </div>

          <div className="trust-badges">
            <div className="badge">
              <span className="badge-icon">🔒</span>
              <span>{t('seo.common.secure')}</span>
            </div>
            <div className="badge">
              <span className="badge-icon">📴</span>
              <span>{t('seo.common.offline')}</span>
            </div>
            <div className="badge">
              <span className="badge-icon">🆓</span>
              <span>{t('seo.common.freeBadge')}</span>
            </div>
            <div className="badge">
              <span className="badge-icon">🌍</span>
              <span>21 {t('seo.common.languages')}</span>
            </div>
          </div>
        </div>
      </section>

      {/* Related Tools Section (Internal Linking) */}
      <section className="seo-related-tools">
        <h2>{t('seo.common.relatedTools')}</h2>
        <div className="related-tools-grid">
          {getRelatedVariants(variant).map((relatedVariant) => {
            const slug = getSlugForLanguage(relatedVariant, language);
            const href = language === 'en' ? `/${slug}/` : `/${language}/${slug}/`;
            return (
              <a key={relatedVariant} href={href} className="related-tool-card">
                <h3>{t(`variants.${relatedVariant}.title`)}</h3>
                <p>{t(`variants.${relatedVariant}.description`)}</p>
                <span className="cta">{t('seo.common.tryNow')} →</span>
              </a>
            );
          })}
        </div>
      </section>

      {/* CTA Section */}
      <section className="seo-final-cta">
        <h2>{t('seo.common.readyToStart')}</h2>
        <p>{t('seo.common.ctaText')}</p>
        <button className="cta-button primary" onClick={() => window.scrollTo({top: 0, behavior: 'smooth'})}>
          {t('seo.common.startSpinning')} ↑
        </button>
      </section>

      {/* Structured Data - JSON-LD */}
      <script type="application/ld+json">
        {JSON.stringify(generateStructuredData(variant, content))}
      </script>
    </div>
  );
};

// Get 3 related variants (valid variant keys) for internal linking.
// Titles/descriptions/slugs are resolved from i18n at render time.
const RELATED_VARIANTS = {
  main: ['namePicker', 'yesNo', 'teamGenerator'],
  yesNo: ['foodWheel', 'moviePicker', 'activityWheel'],
  namePicker: ['teamGenerator', 'giveawayWheel', 'yesNo'],
  teamGenerator: ['namePicker', 'numberWheel', 'sportsWheel'],
  numberWheel: ['letterWheel', 'colorWheel', 'giveawayWheel'],
  letterWheel: ['numberWheel', 'colorWheel', 'questionWheel'],
  colorWheel: ['letterWheel', 'numberWheel', 'activityWheel'],
  foodWheel: ['yesNo', 'activityWheel', 'moviePicker'],
  moviePicker: ['foodWheel', 'gamePicker', 'musicWheel'],
  gamePicker: ['moviePicker', 'truthOrDare', 'activityWheel'],
  giveawayWheel: ['namePicker', 'numberWheel', 'casinoWheel'],
  truthOrDare: ['drinkingGame', 'questionWheel', 'gamePicker'],
  questionWheel: ['truthOrDare', 'letterWheel', 'activityWheel'],
  casinoWheel: ['giveawayWheel', 'numberWheel', 'drinkingGame'],
  datePicker: ['numberWheel', 'countryWheel', 'activityWheel'],
  countryWheel: ['datePicker', 'musicWheel', 'activityWheel'],
  drinkingGame: ['truthOrDare', 'casinoWheel', 'gamePicker'],
  musicWheel: ['moviePicker', 'countryWheel', 'activityWheel'],
  sportsWheel: ['teamGenerator', 'activityWheel', 'countryWheel'],
  activityWheel: ['foodWheel', 'moviePicker', 'yesNo'],
};

const getRelatedVariants = (currentVariant) =>
  RELATED_VARIANTS[currentVariant] || ['namePicker', 'teamGenerator', 'yesNo'];

// Generate enhanced structured data
const generateStructuredData = (variant, content) => {
  return {
    "@context": "https://schema.org",
    "@type": "WebApplication",
    "name": content.hero?.title || `${variant} Wheel`,
    "url": `https://wheel.expert/${variant}/`,
    "description": content.hero?.description,
    "applicationCategory": "UtilitiesApplication",
    "operatingSystem": "Any",
    "offers": {
      "@type": "Offer",
      "price": "0",
      "priceCurrency": "USD"
    },
    "featureList": Array.isArray(content.benefits) ? content.benefits.map(b => b.title) : []
  };
};

export default SEOContentSections;
